import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { z } from 'zod';
import { NavIcon, PlusIcon } from '~/components/icons';
import { CheckinSheet } from '~/components/sheets/checkin-sheet';
import { MenuSheet } from '~/components/sheets/menu-sheet';
import { RegisterSheet } from '~/components/sheets/register-sheet';
import { useSheet } from '~/lib/sheet';

const tabsSearch = z.object({
  sheet: z.enum(['menu', 'checkin', 'register']).optional(),
  /** check-in sheet tab */
  ci: z.enum(['groups', 'people']).optional(),
});
export type TabsSearch = z.infer<typeof tabsSearch>;

export const Route = createFileRoute('/_app/_tabs')({
  validateSearch: tabsSearch,
  component: TabsLayout,
});

/** Hub · Timeline · [+] · Students · Me, with the sheets layered over whichever tab is open. */
function TabsLayout() {
  const { sheet, ci } = Route.useSearch();
  const { open, close } = useSheet();

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <Outlet />
      <BottomNav onPlus={() => open('menu')} />
      {sheet === 'menu' && <MenuSheet onClose={close} />}
      {sheet === 'checkin' && <CheckinSheet tab={ci ?? 'groups'} onClose={close} />}
      {sheet === 'register' && <RegisterSheet onClose={close} />}
    </div>
  );
}

const LEFT = [
  { to: '/hub', label: 'Hub', kind: 'hub' },
  { to: '/timeline', label: 'Timeline', kind: 'tl' },
] as const;
const RIGHT = [
  { to: '/students', label: 'Students', kind: 'stu' },
  { to: '/me', label: 'Me', kind: 'me' },
] as const;

function BottomNav({ onPlus }: { onPlus: () => void }) {
  const item = (n: (typeof LEFT)[number] | (typeof RIGHT)[number]) => (
    <Link
      key={n.to}
      to={n.to}
      className="flex flex-1 flex-col items-center gap-[3px] py-[5px] no-underline"
      activeProps={{ style: { color: '#0369a1' } }}
      inactiveProps={{ style: { color: '#a3acbb' } }}
    >
      {({ isActive }) => (
        <>
          <div className="h-[22px] w-[22px]">
            <NavIcon kind={n.kind} on={isActive} />
          </div>
          <span className="text-[10.5px] font-semibold">{n.label}</span>
        </>
      )}
    </Link>
  );
  return (
    <nav
      className="absolute right-[14px] left-[14px] z-20 flex items-center rounded-[24px] border border-line-soft bg-white px-1.5 py-[7px] shadow-[0_10px_30px_rgba(15,23,42,0.10)]"
      style={{ bottom: 'max(calc(var(--sab) - 6px), 14px)' }}
    >
      {LEFT.map(item)}
      <button
        type="button"
        onClick={onPlus}
        aria-label="Check in or register"
        className="-mt-7 flex h-14 w-14 shrink-0 cursor-pointer items-center justify-center rounded-[19px] border-4 border-white bg-brand text-white shadow-[0_10px_24px_rgba(3,105,161,0.38)]"
      >
        <PlusIcon size={24} />
      </button>
      {RIGHT.map(item)}
    </nav>
  );
}
